// Reunderwriting Module (Tier 3) PR 3 — "Send to reunderwriting" launch
// modal. Lives on the loan-detail header (added via LoanHeader). Posts
// to POST /api/loans/{loanId}/reunderwrite (the PR 1 endpoint) and
// navigates to the case-detail page on success.
//
// The form is intentionally narrow: event type + subject party fields +
// launch reason. Subject-party fields auto-switch off the event type's
// SubjectParty classification ("current borrower" vs "new entity").

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { Button, Card } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { reunderwritingCasesApi } from "@/api/reunderwriting";
import { useReunderwritingEventTypes } from "@/hooks/reunderwriting/useReunderwriting";
import type { CounterpartyKind } from "@/types/reunderwriting";

interface Props {
  loanId: string;
  open: boolean;
  onClose: () => void;
}

export function ReunderwritingLaunchModal({ loanId, open, onClose }: Props) {
  const navigate = useNavigate();
  const toast = useToast();
  const events = useReunderwritingEventTypes(open);

  const [eventTypeCode, setEventTypeCode] = useState("");
  const [kind, setKind] = useState<CounterpartyKind>("Entity");
  const [subjectId, setSubjectId] = useState("");
  const [launchReason, setLaunchReason] = useState("");

  const cfg = useMemo(
    () => (events.data ?? []).find((e) => e.eventTypeCode === eventTypeCode),
    [events.data, eventTypeCode],
  );

  // When the event type changes, auto-pick a sensible default Kind:
  //   "current borrower" → existing borrower likely an Entity.
  //   "new entity"       → new buyer entity.
  useEffect(() => {
    if (!cfg) return;
    setKind("Entity");
  }, [cfg]);

  const launch = useMutation({
    mutationFn: () =>
      reunderwritingCasesApi.launch(loanId, {
        eventTypeCode,
        subjectCounterpartyKind: kind,
        subjectPersonId: kind === "Person" ? subjectId : undefined,
        subjectEntityId: kind === "Entity" ? subjectId : undefined,
        subjectBusinessId: kind === "Business" ? subjectId : undefined,
        launchReason,
      }),
    onSuccess: (created) => {
      toast.success(`Case ${created.caseNumber} launched.`);
      onClose();
      navigate(`/reunderwriting/cases/${created.id}`);
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });

  if (!open) return null;

  const valid =
    !!eventTypeCode && !!subjectId.trim() && launchReason.trim().length > 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Send to reunderwriting"
      className="fixed inset-0 z-50 bg-onyx/40 flex items-center justify-center p-space-4"
      data-testid="reunderwriting-launch-modal"
    >
      <Card className="w-full max-w-[520px] max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-space-3">
          <div>
            <p className="text-label uppercase tracking-label text-gold-shadow m-0">
              Reunderwriting
            </p>
            <h3 className="font-display text-h3 m-0">Send to reunderwriting</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-mid hover:text-onyx text-2xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="mt-space-3 flex flex-col gap-space-3">
          <label className="flex flex-col gap-space-1">
            <span className="text-label uppercase tracking-label text-gold-shadow">
              Event type <span className="text-status-error">*</span>
            </span>
            <Select
              value={eventTypeCode}
              onChange={(e) => setEventTypeCode(e.target.value)}
              data-testid="reunderwriting-launch-eventtype"
            >
              <option value="">Select event type…</option>
              {(events.data ?? []).map((et) => (
                <option key={et.eventTypeCode} value={et.eventTypeCode}>
                  {et.eventTypeCode} ({et.subjectParty})
                </option>
              ))}
            </Select>
            {cfg && (
              <span className="text-caption text-gray-mid">
                Authority: {cfg.authority} · downstream effect:{" "}
                {cfg.downstreamLoanEffect}
              </span>
            )}
          </label>

          <fieldset className="flex flex-col gap-space-1">
            <legend className="text-label uppercase tracking-label text-gold-shadow">
              Subject party kind
            </legend>
            <div className="flex gap-space-3">
              {(["Person", "Entity", "Business"] as const).map((k) => (
                <label key={k} className="inline-flex items-center gap-space-1 text-small">
                  <input
                    type="radio"
                    checked={kind === k}
                    onChange={() => setKind(k)}
                  />
                  {k}
                </label>
              ))}
            </div>
          </fieldset>

          <label className="flex flex-col gap-space-1">
            <span className="text-label uppercase tracking-label text-gold-shadow">
              Subject {kind.toLowerCase()} ID <span className="text-status-error">*</span>
            </span>
            <input
              className="border border-border-light rounded-md p-space-2 text-small font-mono"
              value={subjectId}
              onChange={(e) => setSubjectId(e.target.value)}
              placeholder="00000000-0000-0000-0000-000000000000"
              data-testid="reunderwriting-launch-subject-id"
            />
          </label>

          <label className="flex flex-col gap-space-1">
            <span className="text-label uppercase tracking-label text-gold-shadow">
              Launch reason <span className="text-status-error">*</span>
            </span>
            <textarea
              className="border border-border-light rounded-md p-space-2 text-small min-h-[100px]"
              value={launchReason}
              onChange={(e) => setLaunchReason(e.target.value)}
              placeholder="Why are we opening this reunderwrite?"
              data-testid="reunderwriting-launch-reason"
            />
          </label>
        </div>

        <div className="mt-space-3 flex items-center justify-end gap-space-2">
          <Button variant="tertiary" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => launch.mutate()}
            disabled={!valid || launch.isPending}
            data-testid="reunderwriting-launch-submit"
          >
            Launch case
          </Button>
        </div>
      </Card>
    </div>
  );
}
